import { Bot, Headphones, UserCheck, FileText, Filter, Ticket } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

import kiDialogeImg from "@/assets/inbox-features/ki-dialoge.png";
import liveUebergabeImg from "@/assets/inbox-features/live-uebergabe.png";
import teamZuweisungImg from "@/assets/inbox-features/team-zuweisung.png";
import vorlagenMedienImg from "@/assets/inbox-features/vorlagen-medien.png";
import filterTicketingImg from "@/assets/inbox-features/filter-ticketing.png";
import kiQualitaetImg from "@/assets/inbox-features/ki-qualitaet.png";

const InboxFeaturesSection = () => {
  const { ref, isVisible } = useScrollAnimation();

  const features = [
    {
      icon: Bot,
      title: "KI-geführte Dialoge",
      description: "AI Agents führen Gespräche automatisiert auf Basis der Wissensdatenbank – kontextbezogen und selbstständig.",
      image: kiDialogeImg,
    },
    {
      icon: Headphones,
      title: "Live-Agent-Übergabe",
      description: "Mitarbeiter können jederzeit zugeschaltet werden – mit vollständigem Gesprächsverlauf und Kontext.",
      image: liveUebergabeImg,
    },
    {
      icon: UserCheck,
      title: "Team-Zuweisung & Notizen",
      description: "Dialoge an Teams oder einzelne Mitarbeiter zuweisen, ergänzt durch interne Notizen pro Konversation.",
      image: teamZuweisungImg,
    },
    {
      icon: FileText,
      title: "Vorlagen & Medienversand",
      description: "Vordefinierte Nachrichtenvorlagen, Bilder, Dokumente und Mediendateien direkt im Chat versenden.",
      image: vorlagenMedienImg,
    },
    {
      icon: Filter,
      title: "Filter & Ticketing",
      description: "Offene Dialoge, laufende Gespräche und Tickets filtern – mit klassischer Ticketing-Logik für Support.",
      image: filterTicketingImg,
    },
    {
      icon: Ticket,
      title: "KI-Qualitätsoptimierung",
      description: "KI-geführte Dialoge gezielt selektieren und analysieren, um die Antwortqualität kontinuierlich zu verbessern.",
      image: kiQualitaetImg,
    },
  ];

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className={cn(
        "py-16 md:py-24 bg-secondary/30 transition-all duration-700 ease-out",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      )}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4 text-sm font-semibold text-primary uppercase tracking-wider">
              Funktionen
            </Badge>
            <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4">
              Alles, was Ihr Team für effiziente Kommunikation braucht
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Von KI-Automatisierung bis Live-Chat-Management – alle Funktionen in einer Oberfläche.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className={cn(
                  "bg-card rounded-2xl border border-border transition-all hover:shadow-md overflow-hidden",
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                )}
                style={{ transitionDelay: isVisible ? `${index * 100}ms` : "0ms", transitionDuration: "500ms" }}
              >
                <div className="aspect-[5/3] bg-secondary/50 overflow-hidden">
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <div className="w-10 h-10 rounded-xl bg-chathero-mint/20 flex items-center justify-center mb-4">
                    <feature.icon className="w-5 h-5 text-chathero-mint" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default InboxFeaturesSection;
